import {v4 as uuid} from 'uuid';
import moment from "moment";
import {LibWordArray, MD5} from 'crypto-js';
import axios from "axios";
import {SettingsContext} from "@/utilities/settings-context";
import { i18n } from "@/utilities/vue-i18n";
import { TranslateResult } from 'vue-i18n';

export function truncate(name: string | TranslateResult, length: number): string {
    if(!name) {
        return '…';
    }

    const nameValue = typeof(name) === 'string' ? name : (name as Record<string, unknown>).toString();

    if (nameValue.trim().length <= length + 1) {

        return nameValue;
    }

    return `${(nameValue.slice(0, length) as string).trim()}…`;
}

export function getUniqueId() {

    return uuid();
}

export function findAncestor(element: Element, selector: string) {

    let el = element as Element | null | undefined;

    // noinspection StatementWithEmptyBodyJS
    while ((el = el?.parentElement) && !((el.matches || (el as unknown as {matchesSelector: (e: Element, s: string) => void}).matchesSelector).call(el,selector)));

    return el;
}

export function getCurrentTimestamp() {

    return moment().utc().format('YYYY-MM-DD HH:mm:ss');
}

const colors = [
    '#868C7B', '#D9A86C',
    '#F2D3AC', '#F2A488',
    '#BF7EBB', '#D9D5F2',
    '#58608C', '#2E9AA6',
    '#1D6A73', '#238C6E'
];

export function getUserColor(firstName: string, lastName: string, id: string) {

    const getCode = (text: string) => (MD5(text.toLowerCase()) as unknown as LibWordArray).words.reduce((a, b) => a + b, 0);

    const code = getCode(firstName) + getCode(lastName) + getCode(id);

    return colors[Math.abs(code) % 10];
}

export function getUserPreferences() {
    return axios.get<{dateTimeFormat: string; locale: string}>(SettingsContext.getApiUrl('userPreferences')).then(value => {
        return value.data;
    }).catch();
}

export function invertColor(hex: string, bw: boolean, black = '#000000', white = '#FFFFFF') {

    function padZero(str: string, len = 2) {
        const zeros = new Array(len).join('0');
        return (zeros + str).slice(-len);
    }

    if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
        throw new Error('Invalid HEX color.');
    }
    const r = parseInt(hex.slice(0, 2), 16),
        g = parseInt(hex.slice(2, 4), 16),
        b = parseInt(hex.slice(4, 6), 16);
    if (bw) {
        // http://stackoverflow.com/a/3943023/112731
        return (r * 0.299 + g * 0.587 + b * 0.114) > 186
            ? black
            : white;
    }
    // invert color components
    const rC = (255 - r).toString(16);
    const gC = (255 - g).toString(16);
    const bC = (255 - b).toString(16);
    // pad each with zeros and return
    return "#" + padZero(rC) + padZero(gC) + padZero(bC);
}


export const getDefaultPreferences = () => ({

    format: 'YYYY-MM-DD',

    locale: 'sv'
} as const);

export function setPreferences(preferences: {dateTimeFormat: string; locale: string; disabledHours: Array<string>; disabledWeekly: Array<number>; interval: number}): void {

    getUserPreferences()
    .then(value => {

        preferences.dateTimeFormat = value.dateTimeFormat;

        preferences.locale = value.locale;

    })
    .catch(() => {

        preferences.dateTimeFormat = 'YYYY-MM-DD HH:mm';

        preferences.locale = 'sv';

    })

    axios.get<{disabledHours: Array<string>; disabledWeekly: Array<number>; timePickerInterval: number}>(SettingsContext.getApiUrl('preferences')).then(value => {

        preferences.disabledHours = value.data.disabledHours;

        preferences.disabledWeekly = value.data.disabledWeekly;

        preferences.interval = value.data.timePickerInterval;

    }).catch(() => {

        preferences.disabledHours = [
            "22",
            "23",
            "24",
            "00",
            "01",
            "02",
            "03",
            "04",
            "05"
            ];

        preferences.disabledWeekly = [
            0,
            6
        ];

        preferences.interval = 15;
    });
}

export function extractErrors(error: { response: { data: unknown } | undefined } | unknown): string {

    const getErrors = (obj: object) => {

        const errors = new Array<string>();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const property in (obj as any))
        {
            if (Object.prototype.hasOwnProperty.call(obj, property)){

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const value = (obj as { [key: string]: string })[property] as any;

                if (typeof (value) === 'string')
                {
                    errors.push(value);
                }
                else if (value instanceof Array)
                {
                    const multiple = value.length > 1;

                    for (const error in value)
                    {
                        errors.push(`${multiple ? '• ' : ''}${value[error]}`);
                    }
                }
                else if (typeof (value) === 'object')
                {
                    const innerErrors = getErrors(value);

                    const multiple = innerErrors.length > 1;

                    for (const error in innerErrors)
                    {
                        errors.push(`${multiple ? '• ' : ''}${innerErrors[error]}`);
                    }
                }
            }
        }

        return errors;
    }

    let data = (error as { response: { data: unknown } | undefined })?.response?.data;

    if (data != null && typeof (data) !== 'undefined') {

        if (typeof (data) === 'string') {

            return data;

        } else {

            let errors = new Array<string>();

            if (typeof (data) === 'object') {

                if ((data as {errors: unknown}).errors)
                {
                    data = (data as {errors: unknown}).errors;
                }

                errors = getErrors(data as object);

                if (errors.length > 0)
                {
                    return errors.reduce((previousValue, currentValue) => `${previousValue}<br>${currentValue}`);
                }
            }
        }
    }

    return i18n.t('dialogue.error-general') as string;
}

let div = document.createElement('div');

export function stringifyHtml(html: string | null): string | null {
    div = div || document.createElement('div');
    if (html != null){
        div.innerHTML = html;
        return (div.textContent || div.innerText || '').trim();
    }
    else
    {
        return null;
    }
}

export function trim( str: string, charlist: string ) {
    if ( typeof charlist == 'undefined' ) {
        charlist = '\\s';
    }

    const pattern = '^[' + charlist + ']*(.*?)[' + charlist + ']*$';

    return str.replace( new RegExp( pattern ) , '$1' )
}

export function replaceSpecialCharacters(str: string, replaceWith = '-') {
    if(!str) {
        return str;
    }

    replaceWith = replaceWith.toLowerCase();

    let result = str.toLowerCase().replace(/[^a-zA-Z0-9_öäåÖÄÅ]+/g, replaceWith);

    // normalize for consecutive special characters.
    for(let i = 1; i < result.length; i++) {
        const repeated = replaceWith.repeat(i);

        const index = result.indexOf(repeated);

        if(index >= 0) {
            result = result.slice(0, index) + result.slice(index);
        }
    }

    result = trim(result, replaceWith);

    result = result.trim();

    return result;
}

export function getAdUrl(id: number, title: string) {
    const result = `${SettingsContext.get('ledigajobbWeb')}jobb/` + `${(id).toString(16)}/${replaceSpecialCharacters(title)}`;

    return result.toLowerCase();
}